import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowLeft, Palette, Home, TreePine, Droplets, Sparkles, Camera } from 'lucide-react';
import { brandTheme } from '../../styles/BrandTheme';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${brandTheme.colors.primary[600]};
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: ${brandTheme.colors.primary[700]};
    gap: 0.75rem;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-4px);
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 3rem;

  .category {
    display: inline-block;
    background: ${brandTheme.colors.primary[100]};
    color: ${brandTheme.colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${brandTheme.borderRadius.full};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 1.2;
    margin-bottom: 1.5rem;
    color: ${brandTheme.colors.secondary[900]};
  }

  .excerpt {
    font-size: 1.25rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .meta {
    display: flex;
    gap: 2rem;
    align-items: center;
    color: ${brandTheme.colors.neutral.gray[500]};
    font-size: 0.95rem;
    flex-wrap: wrap;

    svg {
      width: 18px;
      height: 18px;
      margin-right: 0.5rem;
    }

    @media (max-width: 768px) {
      gap: 1rem;
    }
  }
`;

const ArticleImage = styled.div`
  height: 400px;
  background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
  border-radius: ${brandTheme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(59,130,246,0.1)"/></svg>');
  }

  .placeholder {
    font-size: 4rem;
    color: ${brandTheme.colors.primary[300]};
  }
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${brandTheme.colors.secondary[700]};

  h2 {
    font-size: 1.75rem;
    margin: 3rem 0 1.5rem 0;
    color: ${brandTheme.colors.secondary[900]};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: -1.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 24px;
      background: linear-gradient(180deg, ${brandTheme.colors.primary[500]}, ${brandTheme.colors.primary[700]});
      border-radius: 2px;

      @media (max-width: 768px) {
        left: -1rem;
      }
    }

    @media (max-width: 768px) {
      font-size: 1.5rem;
      margin-left: 1rem;
    }
  }

  h3 {
    font-size: 1.4rem;
    margin: 2.5rem 0 1rem 0;
    color: ${brandTheme.colors.secondary[900]};
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul, ol {
    margin: 1.5rem 0;
    padding-left: 2rem;

    li {
      margin-bottom: 0.75rem;
    }
  }
`;

const DesignGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DesignCard = styled.div`
  background: white;
  border-radius: ${brandTheme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }

  .design-image {
    height: 200px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="1.5" fill="rgba(59,130,246,0.1)"/></svg>');
    }

    .placeholder {
      font-size: 2.5rem;
      color: ${brandTheme.colors.primary[300]};
    }
  }

  .design-content {
    padding: 1.5rem;

    h4 {
      font-size: 1.25rem;
      margin-bottom: 0.75rem;
      color: ${brandTheme.colors.secondary[900]};
    }

    p {
      color: ${brandTheme.colors.secondary[600]};
      margin-bottom: 1rem;
      line-height: 1.5;
      font-size: 0.95rem;
    }

    .design-features {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;

      span {
        background: ${brandTheme.colors.primary[100]};
        color: ${brandTheme.colors.primary[700]};
        padding: 0.25rem 0.5rem;
        border-radius: ${brandTheme.borderRadius.sm};
        font-size: 0.8rem;
        font-weight: 500;
      }
    }
  }
`;

const InfoBox = styled.div<{ type?: 'warning' | 'tip' | 'success' }>`
  background: ${props => props.type === 'warning' ? '#fef3c7' : props.type === 'tip' ? '#dbeafe' : '#f0fdf4'};
  border: 1px solid ${props => props.type === 'warning' ? '#fbbf24' : props.type === 'tip' ? '#FECE1B' : '#22c55e'};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  svg {
    color: ${props => props.type === 'warning' ? '#f59e0b' : props.type === 'tip' ? '#FECE1B' : '#16a34a'};
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  div {
    h4 {
      margin: 0 0 0.5rem 0;
      color: ${props => props.type === 'warning' ? '#92400e' : props.type === 'tip' ? '#d97706' : '#14532d'};
      font-size: 1rem;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: ${props => props.type === 'warning' ? '#92400e' : props.type === 'tip' ? '#d97706' : '#14532d'};
    }
  }
`;

const ColorPalette = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin: 2rem 0;

  .color-swatch {
    text-align: center;
    padding: 1rem;
    border-radius: ${brandTheme.borderRadius.lg};
    color: white;
    font-weight: 600;
    font-size: 0.9rem;

    &.warm-grays { background: linear-gradient(135deg, #8B7355, #A0956B); }
    &.cool-grays { background: linear-gradient(135deg, #6B7280, #9CA3AF); }
    &.earth-tones { background: linear-gradient(135deg, #92400E, #B45309); }
    &.blues { background: linear-gradient(135deg, #1E40AF, #3B82F6); }
    &.greens { background: linear-gradient(135deg, #166534, #22C55E); }
    &.terracotta { background: linear-gradient(135deg, #DC2626, #EF4444); }
  }
`;

const CTA = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 2rem;
  text-align: center;
  color: white;
  margin: 3rem 0;

  h3 {
    color: white;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 2rem;
      border-radius: ${brandTheme.borderRadius.lg};
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;

      &.primary {
        background: white;
        color: ${brandTheme.colors.primary[600]};

        &:hover {
          background: ${brandTheme.colors.primary[50]};
          transform: translateY(-2px);
        }
      }

      &.secondary {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: 2px solid rgba(255, 255, 255, 0.3);

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }
`;

const ResinPathwayDesignIdeasPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>25 Stunning Resin Pathway Design Ideas for Your Garden | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Transform your garden with these beautiful resin pathway designs. From modern minimalist to rustic charm, find inspiration for your outdoor space with 25+ design ideas." />
        <meta name="keywords" content="resin pathway design ideas, garden path designs, resin bound pathways, outdoor pathway inspiration, garden walkway ideas, resin surfacing design" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/blog/resin-pathway-design-ideas/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="25 Stunning Resin Pathway Design Ideas for Your Garden" />
        <meta property="og:description" content="Transform your garden with these beautiful resin pathway designs. From modern minimalist to rustic charm, find inspiration for your outdoor space." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/blog/resin-pathway-design-ideas/" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-pathway-design-ideas-og.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Resin pathway design ideas for gardens" />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:published_time" content="2025-01-10T00:00:00+00:00" />
        <meta property="article:modified_time" content="2025-01-10T00:00:00+00:00" />
        <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:section" content="Design Ideas" />
        <meta property="article:tag" content="resin pathway design" />
        <meta property="article:tag" content="garden path designs" />
        <meta property="article:tag" content="resin bound pathways" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="25 Stunning Resin Pathway Design Ideas for Your Garden" />
        <meta name="twitter:description" content="Transform your garden with these beautiful resin pathway designs. From modern minimalist to rustic charm, find inspiration for your outdoor space." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-pathway-design-ideas-og.jpg" />
        <meta name="twitter:image:alt" content="Resin pathway design ideas for gardens" />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="en-GB" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "25 Stunning Resin Pathway Design Ideas for Your Garden",
            "description": "Transform your garden with these beautiful resin pathway designs. From modern minimalist to rustic charm, find inspiration for your outdoor space with 25+ design ideas.",
            "url": "https://www.resinsurfaceslondon.co.uk/blog/resin-pathway-design-ideas/",
            "datePublished": "2025-01-10T00:00:00+00:00",
            "dateModified": "2025-01-10T00:00:00+00:00",
            "author": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              },
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "London",
                "addressCountry": "GB"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.resinsurfaceslondon.co.uk/blog/resin-pathway-design-ideas/"
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://www.resinsurfaceslondon.co.uk/images/resin-pathway-design-ideas-og.jpg",
              "width": 1200,
              "height": 630
            },
            "wordCount": 3200,
            "timeRequired": "PT10M",
            "articleSection": "Design Ideas",
            "keywords": "resin pathway design, garden path designs, resin bound pathways, pathway ideas, garden landscaping, outdoor design",
            "inLanguage": "en-GB",
            "isAccessibleForFree": true,
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": "h1, h2, h3, .excerpt"
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Resin Pathway Design"
              },
              {
                "@type": "Thing", 
                "name": "Garden Landscaping"
              },
              {
                "@type": "Thing",
                "name": "Outdoor Design"
              }
            ]
          })}
        </script>
      </Helmet>

      <ArticleContainer>
        <BackLink to="/blog/">
          <ArrowLeft size={18} />
          Back to Blog
        </BackLink>

        <ArticleHeader>
          <span className="category">Design Ideas</span>
          <h1>25 Stunning Resin Pathway Design Ideas for Your Garden</h1>
          <p className="excerpt">
            Transform your garden with these beautiful resin pathway designs. From modern minimalist to rustic charm, find inspiration for your outdoor space.
          </p>
          <div className="meta">
            <span>
              <Calendar size={18} />
              January 10, 2025
            </span>
            <span>
              <Clock size={18} />
              10 min read
            </span>
            <span>
              <Palette size={18} />
              Design Inspiration
            </span>
          </div>
        </ArticleHeader>

        <ArticleImage>
          <img 
            src="/images/resin-surfaces-bound-work7.jpeg" 
            alt="Resin pathway design ideas showing beautiful garden path designs"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit'
            }}
          />
        </ArticleImage>

        <ArticleContent>
          <p>
            Your garden pathway is more than just a functional route – it's an opportunity to create visual interest, define spaces, and enhance the overall aesthetic of your outdoor area. Resin bound pathways offer unparalleled design flexibility, allowing you to create stunning walkways that complement your garden's style while providing durability and low maintenance.
          </p>

          <h2>Why Choose Resin Bound for Garden Pathways?</h2>

          <p>
            Before we explore the design ideas, let's understand why resin bound surfacing is perfect for garden pathways:
          </p>

          <ul>
            <li><strong>Design Flexibility:</strong> Hundreds of aggregate combinations and colors</li>
            <li><strong>Seamless Finish:</strong> No joints or gaps to collect debris</li>
            <li><strong>Excellent Drainage:</strong> Permeable surface prevents water pooling</li>
            <li><strong>Low Maintenance:</strong> No weeding, re-pointing, or joint maintenance</li>
            <li><strong>Durable:</strong> Withstands weather and foot traffic</li>
            <li><strong>Safe:</strong> Non-slip surface even when wet</li>
          </ul>

          <InfoBox type="tip">
            <Palette size={20} />
            <div>
              <h4>Design Tip</h4>
              <p>Consider your garden's existing color palette when choosing pathway colors. Complementary colors create harmony, while contrasting colors add drama.</p>
            </div>
          </InfoBox>

          <h2>Popular Color Palettes for Resin Pathways</h2>

          <ColorPalette>
            <div className="color-swatch warm-grays">Warm Grays</div>
            <div className="color-swatch cool-grays">Cool Grays</div>
            <div className="color-swatch earth-tones">Earth Tones</div>
            <div className="color-swatch blues">Blues</div>
            <div className="color-swatch greens">Greens</div>
            <div className="color-swatch terracotta">Terracotta</div>
          </ColorPalette>

          <h2>25 Stunning Resin Pathway Design Ideas</h2>

          <DesignGrid>
            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🏡</div>
              </div>
              <div className="design-content">
                <h4>1. Classic Natural Stone</h4>
                <p>Traditional warm gray aggregates with natural stone variations create a timeless, elegant pathway that complements any garden style.</p>
                <div className="design-features">
                  <span>Warm Grays</span>
                  <span>Natural Look</span>
                  <span>Versatile</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌊</div>
              </div>
              <div className="design-content">
                <h4>2. Ocean Blues</h4>
                <p>Cool blue aggregates mixed with white create a calming, coastal-inspired pathway perfect for contemporary gardens.</p>
                <div className="design-features">
                  <span>Cool Tones</span>
                  <span>Modern</span>
                  <span>Calming</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🍂</div>
              </div>
              <div className="design-content">
                <h4>3. Autumn Earth</h4>
                <p>Rich browns, oranges, and golds capture the warmth of autumn, creating a cozy pathway that glows in natural light.</p>
                <div className="design-features">
                  <span>Warm Tones</span>
                  <span>Seasonal</span>
                  <span>Cozy</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">✨</div>
              </div>
              <div className="design-content">
                <h4>4. Sparkling Quartz</h4>
                <p>Clear quartz aggregates mixed with colored stones create a subtle sparkle that catches light beautifully throughout the day.</p>
                <div className="design-features">
                  <span>Sparkle Effect</span>
                  <span>Luxury</span>
                  <span>Light Play</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌿</div>
              </div>
              <div className="design-content">
                <h4>5. Forest Green</h4>
                <p>Deep green aggregates with moss-colored variations create a natural pathway that blends seamlessly with lush gardens.</p>
                <div className="design-features">
                  <span>Natural Green</span>
                  <span>Garden Blend</span>
                  <span>Organic</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🔥</div>
              </div>
              <div className="design-content">
                <h4>6. Terracotta Warmth</h4>
                <p>Rich terracotta and rust-colored aggregates create a Mediterranean-inspired pathway with authentic warmth and character.</p>
                <div className="design-features">
                  <span>Warm Orange</span>
                  <span>Mediterranean</span>
                  <span>Character</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌙</div>
              </div>
              <div className="design-content">
                <h4>7. Midnight Charcoal</h4>
                <p>Deep charcoal and black aggregates create a sophisticated, modern pathway that provides striking contrast to light-colored plants.</p>
                <div className="design-features">
                  <span>Dark Tones</span>
                  <span>Modern</span>
                  <span>Contrast</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌸</div>
              </div>
              <div className="design-content">
                <h4>8. Soft Pastels</h4>
                <p>Light pink, cream, and soft gray aggregates create a gentle, romantic pathway perfect for cottage-style gardens.</p>
                <div className="design-features">
                  <span>Pastel Tones</span>
                  <span>Romantic</span>
                  <span>Cottage Style</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">⚡</div>
              </div>
              <div className="design-content">
                <h4>9. Metallic Accents</h4>
                <p>Silver and gold metallic aggregates add luxury and sophistication, creating pathways that shimmer in sunlight.</p>
                <div className="design-features">
                  <span>Metallic</span>
                  <span>Luxury</span>
                  <span>Shimmer</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🏔️</div>
              </div>
              <div className="design-content">
                <h4>10. Mountain Stone</h4>
                <p>Mixed grays, whites, and browns create a rugged, natural pathway reminiscent of mountain trails and hiking paths.</p>
                <div className="design-features">
                  <span>Mixed Grays</span>
                  <span>Natural</span>
                  <span>Rugged</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌊</div>
              </div>
              <div className="design-content">
                <h4>11. River Pebble</h4>
                <p>Smooth, rounded aggregates in various sizes create a pathway that mimics the natural beauty of riverbeds and streams.</p>
                <div className="design-features">
                  <span>Rounded Stones</span>
                  <span>Water Inspired</span>
                  <span>Textured</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌅</div>
              </div>
              <div className="design-content">
                <h4>12. Sunset Gradient</h4>
                <p>Orange, pink, and yellow aggregates blend to create a pathway that captures the warm glow of sunset skies.</p>
                <div className="design-features">
                  <span>Sunset Colors</span>
                  <span>Gradient</span>
                  <span>Warm Glow</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">❄️</div>
              </div>
              <div className="design-content">
                <h4>13. Arctic White</h4>
                <p>Pure white and light gray aggregates create a clean, minimalist pathway that brightens shady garden areas.</p>
                <div className="design-features">
                  <span>Pure White</span>
                  <span>Minimalist</span>
                  <span>Bright</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌺</div>
              </div>
              <div className="design-content">
                <h4>14. Tropical Paradise</h4>
                <p>Vibrant greens, blues, and whites create an exotic pathway perfect for tropical-themed gardens and pool areas.</p>
                <div className="design-features">
                  <span>Tropical Colors</span>
                  <span>Exotic</span>
                  <span>Vibrant</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🏛️</div>
              </div>
              <div className="design-content">
                <h4>15. Classical Marble</h4>
                <p>White and gray marble aggregates create an elegant, classical pathway suitable for formal gardens and traditional homes.</p>
                <div className="design-features">
                  <span>Marble Look</span>
                  <span>Classical</span>
                  <span>Formal</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌋</div>
              </div>
              <div className="design-content">
                <h4>16. Volcanic Ash</h4>
                <p>Dark gray and black aggregates with subtle red accents create a dramatic pathway inspired by volcanic landscapes.</p>
                <div className="design-features">
                  <span>Dark Drama</span>
                  <span>Volcanic</span>
                  <span>Bold</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🍯</div>
              </div>
              <div className="design-content">
                <h4>17. Honey Gold</h4>
                <p>Warm golden and amber aggregates create a luxurious pathway that glows beautifully in both sunlight and artificial light.</p>
                <div className="design-features">
                  <span>Golden Tones</span>
                  <span>Luxury</span>
                  <span>Warm Glow</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌊</div>
              </div>
              <div className="design-content">
                <h4>18. Coastal Pebbles</h4>
                <p>Mixed cream, white, and light gray aggregates create a beach-inspired pathway perfect for coastal gardens.</p>
                <div className="design-features">
                  <span>Coastal</span>
                  <span>Beach Inspired</span>
                  <span>Light Tones</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌌</div>
              </div>
              <div className="design-content">
                <h4>19. Galaxy Night</h4>
                <p>Deep blues and purples with silver sparkles create a pathway that evokes the mystery and beauty of a starry night sky.</p>
                <div className="design-features">
                  <span>Galaxy Colors</span>
                  <span>Mysterious</span>
                  <span>Sparkly</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌾</div>
              </div>
              <div className="design-content">
                <h4>20. Harvest Wheat</h4>
                <p>Golden yellow and cream aggregates create a warm, agricultural-inspired pathway perfect for country gardens.</p>
                <div className="design-features">
                  <span>Wheat Colors</span>
                  <span>Country Style</span>
                  <span>Agricultural</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🦋</div>
              </div>
              <div className="design-content">
                <h4>21. Butterfly Garden</h4>
                <p>Soft purples, pinks, and greens create a pathway that attracts and celebrates the beauty of butterflies and pollinators.</p>
                <div className="design-features">
                  <span>Pollinator Friendly</span>
                  <span>Soft Colors</span>
                  <span>Wildlife</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🏺</div>
              </div>
              <div className="design-content">
                <h4>22. Ancient Pottery</h4>
                <p>Earthenware browns and terracotta reds create a pathway inspired by ancient pottery and archaeological finds.</p>
                <div className="design-features">
                  <span>Earthenware</span>
                  <span>Ancient</span>
                  <span>Archaeological</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌊</div>
              </div>
              <div className="design-content">
                <h4>23. Waterfall Mist</h4>
                <p>Cool grays and whites with subtle blue undertones create a pathway that mimics the mist and foam of waterfalls.</p>
                <div className="design-features">
                  <span>Water Inspired</span>
                  <span>Cool Tones</span>
                  <span>Misty</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🌹</div>
              </div>
              <div className="design-content">
                <h4>24. Rose Garden</h4>
                <p>Soft pinks and greens create a romantic pathway perfect for rose gardens and traditional English cottage gardens.</p>
                <div className="design-features">
                  <span>Romantic</span>
                  <span>Rose Colors</span>
                  <span>English Garden</span>
                </div>
              </div>
            </DesignCard>

            <DesignCard>
              <div className="design-image">
                <div className="placeholder">🎨</div>
              </div>
              <div className="design-content">
                <h4>25. Artist's Palette</h4>
                <p>A creative mix of multiple colors creates a unique, artistic pathway that showcases your personal style and creativity.</p>
                <div className="design-features">
                  <span>Multi-Color</span>
                  <span>Creative</span>
                  <span>Unique</span>
                </div>
              </div>
            </DesignCard>
          </DesignGrid>

          <h2>Design Considerations for Your Pathway</h2>

          <h3>Width and Layout</h3>
          <ul>
            <li><strong>Main Pathways:</strong> 1.2-1.5m wide for comfortable two-way traffic</li>
            <li><strong>Secondary Paths:</strong> 0.8-1m wide for single person use</li>
            <li><strong>Garden Beds:</strong> 0.6m wide for maintenance access</li>
            <li><strong>Curved Paths:</strong> Create natural flow and visual interest</li>
          </ul>

          <h3>Edging and Borders</h3>
          <ul>
            <li><strong>Natural Edging:</strong> Plants, stones, or wooden sleepers</li>
            <li><strong>Formal Edging:</strong> Brick, concrete, or metal edging</li>
            <li><strong>Integrated Design:</strong> Blend seamlessly with surrounding areas</li>
          </ul>

          <h3>Lighting Integration</h3>
          <ul>
            <li><strong>Solar Path Lights:</strong> Eco-friendly and easy installation</li>
            <li><strong>LED Strip Lighting:</strong> Modern, energy-efficient option</li>
            <li><strong>Spotlighting:</strong> Highlight specific areas or features</li>
            <li><strong>In-Ground Lighting:</strong> Integrated into the pathway surface</li>
          </ul>

          <InfoBox type="success">
            <Camera size={20} />
            <div>
              <h4>Photography Tip</h4>
              <p>Resin pathways photograph beautifully. Consider lighting angles and seasonal changes when planning your design for year-round appeal.</p>
            </div>
          </InfoBox>

          <h2>Maintenance for Beautiful Pathways</h2>

          <p>
            To keep your resin pathway looking stunning year-round:
          </p>

          <ul>
            <li><strong>Regular Cleaning:</strong> Sweep weekly, power wash monthly</li>
            <li><strong>Seasonal Care:</strong> Deep clean in spring and autumn</li>
            <li><strong>Stain Removal:</strong> Address spills promptly with appropriate cleaners</li>
            <li><strong>Edge Maintenance:</strong> Keep borders clean and plants trimmed</li>
          </ul>

          <CTA>
            <h3>Ready to Create Your Dream Pathway?</h3>
            <p>
              Transform your garden with a stunning resin bound pathway. Our design experts can help you choose the perfect colors and layout for your space.
            </p>
            <div className="cta-buttons">
              <Link to="/contact/" className="primary">
                <Palette size={18} />
                Get Design Consultation
              </Link>
              <Link to="/gallery/" className="secondary">
                <Camera size={18} />
                View Our Work
              </Link>
            </div>
          </CTA>

          <h2>Conclusion</h2>

          <p>
            A well-designed resin bound pathway can transform your garden from a simple outdoor space into a stunning landscape feature. With 25 design ideas to choose from, you're sure to find the perfect style that complements your garden's personality and your home's architecture.
          </p>

          <p>
            Remember, the best pathway design considers not just aesthetics but also functionality, maintenance, and how it integrates with your overall garden design. Whether you prefer the natural warmth of earth tones or the modern sophistication of cool grays, resin bound surfacing offers the flexibility to bring your vision to life.
          </p>

          <p>
            Take inspiration from these ideas, but don't be afraid to create something uniquely yours. After all, your garden pathway should reflect your personal style and enhance your enjoyment of your outdoor space for years to come.
          </p>
        </ArticleContent>
      </ArticleContainer>
    </>
  );
};

export default ResinPathwayDesignIdeasPage;
